import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";

import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import List from "@material-ui/core/List";
import ListItem from "@material-ui/core/ListItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import TextField from "@material-ui/core/TextField";

import { fetchRedirectionParams } from "../../../actions/redirect";
import { setCardData } from "../../../actions/card";
import { FormatAmount } from "../../../utils/Formatter";
import {
  getCardDisplayNameForScheme,
  getCardIconForScheme
} from "../../../utils/BinUtils";

import "./SavedCard.css";

const isFormValid = form => {
  if (!form.cvv) return { isInvalid: true };
  if (form.cvv.length < 3) return { isInvalid: true, errorMsg: "Too short" };
  return { isInvalid: false };
};

export const SavedCardList = ({ charge, dispatch, history }) => {
  const { customerSavedCards, amount, currency } = charge;
  const [form, setForm] = useState({
    cvv: "",
    id: customerSavedCards[0].id
  });
  const [validation, setValidation] = useState({ isInvalid: false });
  const cvvRef = React.createRef();
  useEffect(() => {
    cvvRef.current.focus();
    setValidation({ isInvalid: false });
    // Disabling eslint for the next line, following it actually causes a ui bug
    // eslint-disable-next-line
  }, [form.id]);

  const validateForm = () => {
    const validationResult = isFormValid(form);
    setValidation(validationResult);
    return validationResult;
  };

  function cardInfo(scheme, maskedPan, expiryDate) {
    const cardNumber = ` ••••${maskedPan}`;
    return (
      <div>
        <span className="saved-card-number">
          <span className="saved-card-name">
            {getCardDisplayNameForScheme(scheme)}
          </span>
          {cardNumber}
        </span>
        {/* eslint-disable-next-line react/jsx-one-expression-per-line */}
        <span className="saved-card-exp-date">({expiryDate})</span>
      </div>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const submitSavedCardForm = e => {
    const validationResult = validateForm();
    if (validationResult.isInvalid) {
      cvvRef.current.focus();
      e.preventDefault();
    } else {
      dispatch(fetchRedirectionParams(charge.uuid, form, "savedcard", history));
      form.type = "savedcard";
      dispatch(setCardData(form));
    }
  };

  const renderSavedCard = ({ id, scheme, maskedPan, expiry }) => {
    const expiryDate = `${expiry.substring(0, 2)}/${expiry.substring(2)}`;

    return (
      <ListItem
        button
        key={id}
        className="saved-card-item"
        onClick={() => {
          setForm({ ...form, id, cvv: "" });
          setValidation({ isInvalid: false });
        }}
      >
        <ListItemIcon className="saved-card-logo">
          <img
            src={getCardIconForScheme(scheme)}
            alt="card-logo"
            style={{ width: 25 }}
          />
        </ListItemIcon>
        <ListItemText primary={cardInfo(scheme, maskedPan, expiryDate)} />
        {id === form.id && (
          <TextField
            id="cvv"
            placeholder="CVV"
            value={form.cvv}
            onChange={e => {
              const { value } = e.target;
              if (/^\d{0,4}$/.test(value)) {
                setForm({ ...form, cvv: value });
                setValidation({ isInvalid: false });
              }
            }}
            onFocus={validateForm}
            inputRef={cvvRef}
            error={validation.isInvalid}
            helperText={validation.errorMsg}
            className="saved-card-cvv"
            type="password"
            inputProps={{ style: { padding: "6px 8px", fontSize: 16 } }}
          />
        )}
      </ListItem>
    );
  };

  const savedCards = [];
  customerSavedCards.forEach((card, index) => {
    savedCards.push(renderSavedCard(card));
    if (customerSavedCards[index + 1]) {
      savedCards.push(<Divider key={card.id} />);
    }
  });
  return (
    <List
      component="nav"
      aria-labelledby="nested-list-subheader"
      align="center"
    >
      {savedCards}
      <Button
        id="pay-button"
        variant="outlined"
        color="primary"
        onClick={submitSavedCardForm}
      >
        Pay&nbsp;
        {FormatAmount(amount, currency)}
      </Button>
    </List>
  );
};

SavedCardList.propTypes = {
  charge: PropTypes.shape({
    uuid: PropTypes.string.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
    customerId: PropTypes.number.isRequired,
    customerSavedCards: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        scheme: PropTypes.string.isRequired,
        maskedPan: PropTypes.string.isRequired,
        expiry: PropTypes.string.isRequired
      }).isRequired
    ).isRequired
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape().isRequired
};

function mapStateToProps({ redirectionReducer }) {
  return redirectionReducer;
}

export default withRouter(connect(mapStateToProps)(SavedCardList));
